import { Definition } from '@rt/shared/src';
import { SharedEnv, type Externals as SharedExternals } from '@rt/shared/src/env';

type Var = SharedEnv.Var & {
  Postgres: {
    Host: string;
    Port: number;
  };
  App: {
    Domain: string;
    BasePath: string;
  };
  Api: {
    Domain: string;
    Port: number;
  };
  InternalApi: {
    Domain: string;
    Port: number;
  };
  InterserviceApi: {
    Domain: string;
    Port: number;
  };
  KarteApi: {
    Port: number;
  };
  FirebaseAuth: {
    ApiKey: string;
    AuthDomain: string;
    ProjectId: string;
    StorageBucket: string;
    MessagingSenderId: string;
    AppId: string;
  };
  Valkey: {
    Host: string;
    Port: number;
    UseIamAuth: boolean;
    InstanceName: string;
  };
  SystemAdminProductEntryDenylist: {
    OrganizationIds: string[];
    ProjectIds: string[];
  };

  Cookie: {
    SessionId: string;
    SessionCsrfToken: string;
    SessionAuth: {
      MaxAgeMs: number;
      IdleTimeoutMs: number;
      IdleTimeoutEnforceDeadlineMs: number;
      IdleTimeoutEnforceMissingKeyGraceMs: number;
    };
  };
  AmazonSES: {
    email: string;
  };
  Dns: {
    Root: {
      Name: string;
      ZoneId: string;
    };
    Qant: {
      Name: string;
      ZoneId: string;
    };
  };
  KarteOidc: {
    RedirectUri: string;
  };
  Datadog:
    | {
        ApplicationId: string;
        ClientToken: string;
        Site: 'us5.datadoghq.com';
        Env: string;
        SessionSampleRate: number;
        SessionReplaySampleRate: number;
        TrackUserInteractions: boolean;
        TrackResources: boolean;
        TrackLongTasks: boolean;
        AllowedTracingUrls: RegExp[];
      }
    | undefined;
};

type Externals = SharedExternals & {
  AUTH_GOOGLE_CLIENT_ID: string;
  AUTH_GOOGLE_CLIENT_SECRET: string;
  AUTH_AMAZON_SES_ACCESS_KEY_ID: string;
  AUTH_AMAZON_SES_ACCESS_KEY_SECRET: string;
  AUTH_ROOT_USER_PASSWORD: string;
  AUTH_KARTE_OIDC_CLIENT_ID: string;
  AUTH_KARTE_OIDC_CLIENT_SECRET: string;
  AUTH_KARTE_OIDC_COOKIE_KEY: string;
  AUTH_KARTE_OIDC_JWK_PRIVATE_KEY: string;
  AUTH_PAM_SYSTEM_ADMIN_ENTITLEMENT_NAME: string;
  AUTH_PAM_PII_ACCESS_ENTITLEMENT_NAME: string;
};

const sessionCookieMaxAgeMs = 1000 * 60 * 60 * 24 * 7;
const sessionCookieIdleTimeoutMs = 1000 * 60 * 60 * 24;
const sessionCookieIdleTimeoutEnforceDeadlineMs = 500;
const sessionCookieIdleTimeoutEnforceMissingKeyGraceMs = 1000 * 60 * 5;

const sessionAuthConfig = (): Var['Cookie']['SessionAuth'] => ({
  MaxAgeMs: sessionCookieMaxAgeMs,
  IdleTimeoutMs: sessionCookieIdleTimeoutMs,
  IdleTimeoutEnforceDeadlineMs: sessionCookieIdleTimeoutEnforceDeadlineMs,
  IdleTimeoutEnforceMissingKeyGraceMs: sessionCookieIdleTimeoutEnforceMissingKeyGraceMs,
});

const valkeyConfig = (defaultInstanceName: string): Var['Valkey'] => ({
  Host: process.env.VALKEY_HOST ?? '',
  Port: Number.parseInt(process.env.VALKEY_PORT ?? '6379', 10),
  UseIamAuth: process.env.VALKEY_USE_IAM_AUTH === 'true',
  InstanceName: process.env.VALKEY_INSTANCE_NAME ?? defaultInstanceName,
});

const parseCommaSeparatedEnv = (value: string | undefined): string[] =>
  Array.from(
    new Set(
      (value ?? '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );

const systemAdminProductEntryDenylistConfig = (): Var['SystemAdminProductEntryDenylist'] => ({
  OrganizationIds: parseCommaSeparatedEnv(process.env.AUTH_SYSTEM_ADMIN_PRODUCT_ENTRY_DENYLIST_ORGANIZATION_IDS),
  ProjectIds: parseCommaSeparatedEnv(process.env.AUTH_SYSTEM_ADMIN_PRODUCT_ENTRY_DENYLIST_PROJECT_IDS),
});

class ThisEnv extends SharedEnv {
  Var: Var;
  Externals: Externals;

  constructor() {
    super();
    const serviceHost = process.env.SERVICE_HOST || 'localhost';
    this.Var = (
      {
        [Definition.Env.Dev]: {
          ...this.BaseVar,
          Postgres: {
            Host: process.env.POSTGRES_HOST ?? serviceHost,
            Port: Number.parseInt(process.env.POSTGRES_PORT ?? '5442', 10),
          },
          App: {
            Domain: `http://${this.BaseVar.QantDomain.auth.app}`,
            BasePath: '/auth',
          },
          Api: {
            Domain: `http://${this.BaseVar.QantDomain.auth.api}`,
            Port: 3031,
          },
          InternalApi: {
            Domain: `http://${this.BaseVar.QantDomain.auth.internalApi}`,
            Port: 3033,
          },
          InterserviceApi: {
            Domain: `http://${this.BaseVar.QantDomain.auth.interserviceApi}`,
            Port: 3032,
          },
          KarteApi: {
            Port: 3033,
          },
          Karte: {
            ...this.BaseVar.Karte,
            issuesApiHost: process.env.KARTE_ISSUES_API_HOST ?? this.BaseVar.Karte.issuesApiHost,
          },
          FirebaseAuth: {
            ApiKey: 'AIzaSyBe6LVfwgVZychAx5Kkprj9SXreyr-V9tA',
            AuthDomain: 'evaluation-rt-systems.firebaseapp.com',
            ProjectId: 'evaluation-rt-systems',
            StorageBucket: 'evaluation-rt-systems.appspot.com',
            MessagingSenderId: '282057777327',
            AppId: '1:282057777327:web:bc165340f86efda118f45a',
          },
          Valkey: valkeyConfig(''),
          SystemAdminProductEntryDenylist: systemAdminProductEntryDenylistConfig(),
          Cookie: {
            SessionId: 'rt_session_dev',
            SessionCsrfToken: 'rt_session_csrf_dev',
            SessionAuth: sessionAuthConfig(),
          },
          AmazonSES: {
            email: 'invitations.qant@righttouch.dev',
          },
          Dns: {
            Root: {
              Name: 'localhost',
              ZoneId: 'not set',
            },
            Qant: {
              Name: 'localhost',
              ZoneId: 'not set',
            },
          },
          KarteOidc: {
            RedirectUri: 'https://admin.karte.test/admin/login/qant/callback',
          },
          Datadog: {
            ApplicationId: 'eafe4f4c-1f2c-4a8a-a9bb-3c5740cc4ef7',
            ClientToken: 'pub0b3ec1280b9dfa7937cfe6c7247648bd',
            Site: 'us5.datadoghq.com',
            Env: 'evaluation',
            SessionSampleRate: 100,
            SessionReplaySampleRate: 20,
            TrackUserInteractions: true,
            TrackResources: true,
            TrackLongTasks: true,
            AllowedTracingUrls: [/http:\/\/localhost/],
          },
        },
        [Definition.Env.Test]: {
          ...this.BaseVar,
          Postgres: {
            Host: 'localhost',
            Port: 5443,
          },
          App: {
            Domain: `http://${this.BaseVar.QantDomain.auth.app}`,
            BasePath: '/auth',
          },
          Api: {
            Domain: `http://${this.BaseVar.QantDomain.auth.api}`,
            Port: 3031,
          },
          InternalApi: {
            Domain: `http://${this.BaseVar.QantDomain.auth.internalApi}`,
            Port: 3033,
          },
          InterserviceApi: {
            Domain: `http://${this.BaseVar.QantDomain.auth.interserviceApi}`,
            Port: 3032,
          },
          KarteApi: {
            Port: 3033,
          },
          FirebaseAuth: {
            ApiKey: 'AIzaSyBe6LVfwgVZychAx5Kkprj9SXreyr-V9tA',
            AuthDomain: 'evaluation-rt-systems.firebaseapp.com',
            ProjectId: 'evaluation-rt-systems',
            StorageBucket: 'evaluation-rt-systems.appspot.com',
            MessagingSenderId: '282057777327',
            AppId: '1:282057777327:web:bc165340f86efda118f45a',
          },
          Valkey: valkeyConfig(''),
          SystemAdminProductEntryDenylist: systemAdminProductEntryDenylistConfig(),
          Cookie: {
            SessionId: 'rt_session_test',
            SessionCsrfToken: 'rt_session_csrf_test',
            SessionAuth: sessionAuthConfig(),
          },
          AmazonSES: {
            email: 'invitations.qant@righttouch.dev',
          },
          Dns: {
            Root: {
              Name: 'localhost',
              ZoneId: 'not set',
            },
            Qant: {
              Name: 'localhost',
              ZoneId: 'not set',
            },
          },
          KarteOidc: {
            RedirectUri: 'https://admin.karte.test/admin/login/qant/callback/test',
          },
          Datadog: undefined,
        },
        [Definition.Env.Eva]: {
          ...this.BaseVar,
          Postgres: {
            Host: '127.0.0.1',
            Port: 5432,
          },
          App: {
            Domain: `https://${this.BaseVar.QantDomain.base.app}`,
            BasePath: '/auth',
          },
          Api: {
            Domain: `https://${this.BaseVar.QantDomain.auth.api}`,
            Port: 8080,
          },
          InternalApi: {
            Domain: `https://${this.BaseVar.QantDomain.auth.internalApi}`,
            Port: 3032,
          },
          InterserviceApi: {
            Domain: `https://${this.BaseVar.QantDomain.auth.interserviceApi}`,
            Port: 3032,
          },
          KarteApi: {
            Port: 8080,
          },
          FirebaseAuth: {
            ApiKey: 'AIzaSyBe6LVfwgVZychAx5Kkprj9SXreyr-V9tA',
            AuthDomain: 'login.eva-qant.jp',
            ProjectId: 'evaluation-rt-systems',
            StorageBucket: 'evaluation-rt-systems.appspot.com',
            MessagingSenderId: '282057777327',
            AppId: '1:282057777327:web:bc165340f86efda118f45a',
          },
          Valkey: valkeyConfig('projects/evaluation-rt-systems/locations/asia-northeast1/instances/eva-auth-valkey'),
          SystemAdminProductEntryDenylist: systemAdminProductEntryDenylistConfig(),
          Cookie: {
            SessionId: '__Secure-rt_session_eva',
            SessionCsrfToken: '__Secure-rt_session_csrf_eva',
            SessionAuth: sessionAuthConfig(),
          },
          AmazonSES: {
            email: 'invitations.qant@righttouch.dev',
          },
          Dns: {
            Root: {
              Name: 'auth.eva.dev-righttouch.com',
              ZoneId: 'Z04897872DMPSSUN1MYA6',
            },
            Qant: {
              Name: 'auth.eva-qant.jp',
              ZoneId: 'Z01774123J8CZZXZEMTBI',
            },
          },
          KarteOidc: {
            RedirectUri: 'https://evaluation.dev-karte.com/admin/login/qant/callback',
          },
          Datadog: {
            ApplicationId: 'eafe4f4c-1f2c-4a8a-a9bb-3c5740cc4ef7',
            ClientToken: 'pub0b3ec1280b9dfa7937cfe6c7247648bd',
            Site: 'us5.datadoghq.com',
            Env: 'evaluation',
            SessionSampleRate: 100,
            SessionReplaySampleRate: 20,
            TrackUserInteractions: true,
            TrackResources: true,
            TrackLongTasks: true,
            AllowedTracingUrls: [/https:\/\/.*\.dev-righttouch\.com/],
          },
        },
        [Definition.Env.Prod]: {
          ...this.BaseVar,
          Postgres: {
            Host: '127.0.0.1',
            Port: 5432,
          },
          App: {
            Domain: `https://${this.BaseVar.QantDomain.base.app}`,
            BasePath: '/auth',
          },
          Api: {
            Domain: `https://${this.BaseVar.QantDomain.auth.api}`,
            Port: 8080,
          },
          InternalApi: {
            Domain: `https://${this.BaseVar.QantDomain.auth.internalApi}`,
            Port: 8080,
          },
          InterserviceApi: {
            Domain: `https://${this.BaseVar.QantDomain.auth.interserviceApi}`,
            Port: 8080,
          },
          KarteApi: {
            Port: 8080,
          },
          FirebaseAuth: {
            ApiKey: 'AIzaSyD7H0lllpWNvqrS5cmr0gRSe2-OB6FlQuw',
            AuthDomain: 'login.qant.jp',
            ProjectId: 'production-rt-systems',
            StorageBucket: 'production-rt-systems.appspot.com',
            MessagingSenderId: '402791595004',
            AppId: '1:402791595004:web:1013109ded264820545079',
          },
          Valkey: valkeyConfig('projects/production-rt-systems/locations/asia-northeast1/instances/prod-auth-valkey'),
          SystemAdminProductEntryDenylist: systemAdminProductEntryDenylistConfig(),
          Cookie: {
            SessionId: '__Secure-rt_session',
            SessionCsrfToken: '__Secure-rt_session_csrf',
            SessionAuth: sessionAuthConfig(),
          },
          AmazonSES: {
            email: 'invitations.qant@righttouch.co.jp',
          },
          Dns: {
            Root: {
              Name: 'auth.dev-righttouch.com',
              ZoneId: 'Z03263632SRKVMH40GP6G',
            },
            Qant: {
              Name: 'auth.qant.jp',
              ZoneId: 'Z06980611QNBHZY6Q7UHU',
            },
          },
          KarteOidc: {
            RedirectUri: 'https://admin.karte.io/admin/login/qant/callback',
          },
          Datadog: {
            ApplicationId: '0201902e-8e6e-44b9-9a99-e3402512f34a',
            ClientToken: 'pub904a8c23b8e33df020cf4531bd661a51',
            Site: 'us5.datadoghq.com',
            Env: 'production',
            SessionSampleRate: 100,
            SessionReplaySampleRate: 20,
            TrackUserInteractions: true,
            TrackResources: true,
            TrackLongTasks: true,
            AllowedTracingUrls: [/https:\/\/.*\.dev-righttouch\.com/],
          },
        },
      } as const satisfies { [K in Definition.Env]: Var }
    )[this.Name];
    this.Externals = Object.assign(this.BaseExternals, {
      AUTH_GOOGLE_CLIENT_ID: process.env.AUTH_GOOGLE_CLIENT_ID || '',
      AUTH_GOOGLE_CLIENT_SECRET: process.env.AUTH_GOOGLE_CLIENT_SECRET || '',
      AUTH_AMAZON_SES_ACCESS_KEY_ID: process.env.AUTH_AMAZON_SES_ACCESS_KEY_ID || '',
      AUTH_AMAZON_SES_ACCESS_KEY_SECRET: process.env.AUTH_AMAZON_SES_ACCESS_KEY_SECRET || '',
      AUTH_ROOT_USER_PASSWORD: process.env.AUTH_ROOT_USER_PASSWORD || 'password123',
      AUTH_KARTE_OIDC_CLIENT_ID: process.env.AUTH_KARTE_OIDC_CLIENT_ID || '',
      AUTH_KARTE_OIDC_CLIENT_SECRET: process.env.AUTH_KARTE_OIDC_CLIENT_SECRET || '',
      AUTH_KARTE_OIDC_COOKIE_KEY: process.env.AUTH_KARTE_OIDC_COOKIE_KEY || '',
      AUTH_KARTE_OIDC_JWK_PRIVATE_KEY: process.env.AUTH_KARTE_OIDC_JWK_PRIVATE_KEY || '',
      AUTH_PAM_SYSTEM_ADMIN_ENTITLEMENT_NAME: process.env.AUTH_PAM_SYSTEM_ADMIN_ENTITLEMENT_NAME || '',
      AUTH_PAM_PII_ACCESS_ENTITLEMENT_NAME: process.env.AUTH_PAM_PII_ACCESS_ENTITLEMENT_NAME || '',
    });
  }

  getResourceName(name = '', env?: Definition.Env): string {
    return super.getResourceName(Definition.System.Auth, name, env || this.Name);
  }
}

export const Env = new ThisEnv();
